import _ from 'lodash';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik, useFormikContext } from 'formik';

import {
	Avatar,
	Input,
	Label,
	Modal,
	Paragraph,
	Popover,
	QuillInput,
	QuillText,
	Select,
	Skeleton,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import FeedbackResponseAnswer from '../respond/FeedbackResponseAnswer';
import { useEffect } from 'react';
import { useHistory } from '@treams/treams-routes';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';

function SendFeedbackQuestions(props: { templateID: string }) {
	const query = graphql.useFeedbackTemplateQuery({ variables: { id: props.templateID } });
	const { localize } = useLocalize();

	const formik = useFormikContext<{
		questions: graphql.FeedbackRequestQuestionInput[];
		answers: graphql.FeedbackResponseAnswerInput[];
	}>();

	useEffect(() => {
		if (!query.data) {
			formik.setFieldValue('questions', []);
			formik.setFieldValue('answers', []);
			return;
		}

		formik.setFieldValue(
			'questions',
			query.data.feedbackTemplate.questions.map((question) => ({
				text: localize(question.question.text).text,
				type: question.question.type,
				description: '',
			})),
		);

		formik.setFieldValue(
			'answers',
			query.data.feedbackTemplate.questions.map((question) => ({
				questionID: question.question.id,
			})),
		);
	}, [!!query.data]);

	if (query.data) {
		return (
			<Box>
				{formik.values.questions.map((question, i) => (
					<FeedbackResponseAnswer
						key={i}
						index={i}
						name={`answers.${i}`}
						question={question as graphql.FeedbackRequestQuestion}
						viewerCanUpdate
					/>
				))}
			</Box>
		);
	} else {
		return (
			<Skeleton.Repeat count={3}>
				<Box mb={3}>
					<Label.Skeleton mb={2} />
					<Input.Skeleton />
				</Box>
			</Skeleton.Repeat>
		);
	}
}

export default function SendFeedbackModal(props: { userID?: string }) {
	const { t } = useTranslation();
	const history = useHistory();

	const { localize } = useLocalize();

	const query = graphql.useFeedbackTemplateSelectorQuery({
		variables: {
			filters: {
				role: graphql.FeedbackTemplateRole.Spontaneous,
			},
		},
	});
	const [send] = graphql.useSendFeedbackMutation();

	const formik = useFormik({
		initialValues: {
			userID: props.userID,
			templateID: undefined as string | undefined,
			title: '' as string,
			instruction: { ops: [] },
			questions: [] as graphql.FeedbackRequestQuestionInput[],
			answers: [] as graphql.FeedbackResponseAnswerInput[],
		},
		validate(values) {
			if (
				!values.userID ||
				!values.templateID ||
				!values.title ||
				!values.instruction.ops
					.map(({ insert }) => insert ?? '')
					.join('')
					.trim()
			) {
				return { error: 'validation' };
			}
		},
		isInitialValid: false,
		onSubmit: async (values) => {
			const response = await send({
				variables: {
					input: {
						recipientID: values.userID!,
						title: values.title,
						instruction: values.instruction,
						questions: values.questions,
						answers: values.answers,
					},
				},
			});

			history.push(`/feedback/given/${response.data!.sendSpontaneousFeedback.feedbackResponse.id}`);
		},
	});

	useEffect(() => {
		if (!query.data) {
			return;
		}

		if (query.data.feedbackTemplates.edges.length === 1) {
			const id = query.data.feedbackTemplates.edges[0].node.id;
			formik.setFieldValue('templateID', id);
		}
	}, [query.data?.feedbackTemplates.edges.length]);

	const { results } = useSearch('', {
		status: graphql.UserStatus.Active,
		excludeViewer: true,
	});

	const url = props.userID ? `/v2/users/${props.userID}/feedback` : `/v2/feedback`;

	const template = query.data?.feedbackTemplates.edges
		.map(({ node }) => node)
		.find((node) => node.id === formik.values.templateID);

	return (
		<Popover visible center>
			<FormikProvider value={formik}>
				<Modal>
					<Modal.Header
						title={t('feedback.spontaneous.modal.title')}
						onClose={() => history.push(url)}
					/>
					<Modal.Contents>
						<Box mb={3}>
							<Label mb={2}>{t('feedback.spontaneous.modal.fields.recipient.label')}:</Label>
							<Flex>
								<Select.Formik
									disabled={!!props.userID}
									placeholder={t('feedback.spontaneous.modal.recipient.placeholder')}
									name="userID"
									options={_.sortBy(results, ['firstName', 'lastName']).map((user) => ({
										icon: <Avatar user={user} size={24} />,
										title: `${user.firstName} ${user.lastName}`,
										value: user.id,
									}))}
								/>
							</Flex>
						</Box>
						{query.data?.feedbackTemplates.edges.length !== 1 && (
							<Box mb={3}>
								<Label mb={2}>{t('feedback.spontaneous.modal.fields.template.label')}:</Label>
								<Flex>
									<Select.Formik
										placeholder={t('feedback.spontaneous.modal.template.placeholder')}
										name="templateID"
										options={query.data?.feedbackTemplates.edges.map(({ node }) => ({
											title: localize(node.title).text,
											value: node.id,
										}))}
									/>
								</Flex>
							</Box>
						)}
						{template?.instruction && localize(template.instruction) && (
							<Box mb={3}>
								<Label mb={2}>
									{t('feedback.spontaneous.modal.fields.templateInstruction.label')}:
								</Label>
								<Paragraph>
									<QuillText value={localize(template.instruction).text} />
								</Paragraph>
							</Box>
						)}
						<Box mb={3}>
							<Label mb={2}>{t('feedback.spontaneous.modal.fields.title.label')}:</Label>
							<Input.Formik name="title" />
						</Box>
						<Box mb={3}>
							<Label mb={2}>{t('feedback.spontaneous.modal.fields.instruction.label')}:</Label>
							<QuillInput.Formik name="instruction" />
						</Box>
						{formik.values.templateID && (
							<SendFeedbackQuestions
								key={formik.values.templateID}
								templateID={formik.values.templateID}
							/>
						)}
					</Modal.Contents>
					<Modal.FormFooter.Formik
						onCancel={() => history.push(url)}
						saveTitle={t('feedback.spontaneous.modal.sendButton.title')}
						disabled={!formik.isValid}
					/>
				</Modal>
			</FormikProvider>
		</Popover>
	);
}
